import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import {
    getEmailSchedulerExceptionsState,
    getExceptionEditProperties,
} from "store/features/emailScheduler/exceptions/exceptions.selectors";
import { useAlert, usePreviousProp, useProcessingStatus } from "hooks";
import {
    cancelExceptionUpdate,
    createSchedulerRuleException,
    editExceptionRule,
    updateSchedulerRuleException,
} from "store/features/emailScheduler/exceptions/exceptions.actions";
import { SchedulerExceptionForm } from "validation/components/EmailScheduler/exceptionsFormRules";
import { useTranslation } from "react-i18next";

export const useExceptionDialog = (handleConfirmationDialogClose: () => void) => {
    const dispatch = useDispatch();
    const [isOpen, setOpen] = useState(false);
    const { isDeleting, hasError } = useSelector(getEmailSchedulerExceptionsState);
    const { isEdited, id } = useSelector(getExceptionEditProperties);
    const { toggleAlert } = useAlert();
    const isDeletingPrev = usePreviousProp(isDeleting);
    const { t } = useTranslation();

    useProcessingStatus(isDeleting, hasError);

    const handleSubmit = (formData: SchedulerExceptionForm) => {
        if (isEdited) {
            dispatch(updateSchedulerRuleException.request({ ...formData, id }));

            return;
        }

        dispatch(createSchedulerRuleException.request(formData));
    };

    const handleDialog = useCallback(
        (isVisible: boolean) => () => {
            if (isEdited) {
                dispatch(cancelExceptionUpdate());
            }

            setOpen(isVisible);
        },
        [dispatch, isEdited]
    );

    const handleEditException = useCallback((name: string) => () => dispatch(editExceptionRule(name)), [dispatch]);

    useEffect(() => {
        if (!isDeleting && isDeletingPrev && !hasError) {
            toggleAlert({ message: t("rules.label.exceptionRemoved"), type: "success" });
            handleConfirmationDialogClose();
        }
    }, [isDeleting, isDeletingPrev, toggleAlert, hasError, handleConfirmationDialogClose, t]);

    useEffect(() => {
        if (isEdited && id) {
            setOpen(true);
        }
    }, [isEdited, id]);

    return {
        handleEditException,
        handleDialog,
        isOpen,
        handleSubmit,
    };
};
